/**
 * Visual variant presets for notification cards. Mirrors Flutter's
 * lib/widgets/notifications/variant_presets.dart — keep keys/colors aligned.
 */

export type NotificationVariant =
  | 'celebration'
  | 'alert'
  | 'coaching'
  | 'progress'
  | 'shift'
  | 'manager'
  | 'system';

export type BennyPose = 'celebrating' | 'thinking' | 'server' | 'avatar';

export interface VariantPreset {
  bgGradient?: [string, string];
  iconBg: string;
  iconColor: string;
  accentBar?: string;
  bennyPose?: BennyPose;
  confettiAccent?: boolean;
  titleColor: string;
  titleClass: string;
  defaultIconKey: IconKey;
}

export function bennyAssetFor(pose?: BennyPose): string | null {
  if (!pose) return null;
  return `/img/benny_${pose}.png`;
}

export type IconKey =
  | 'trophy'
  | 'flame'
  | 'lightbulb'
  | 'trending-up'
  | 'trending-down'
  | 'calendar'
  | 'users'
  | 'bell'
  | 'award'
  | 'sparkles'
  | 'dollar'
  | 'alert-triangle'
  | 'wifi-off'
  | 'party';

const variants: Record<NotificationVariant, VariantPreset> = {
  celebration: {
    bgGradient: ['#FFE5F9', '#FFF9E5'],
    iconBg: '#FFF9E5',
    iconColor: '#F7C948',
    confettiAccent: true,
    bennyPose: 'celebrating',
    titleColor: '#1F2937',
    titleClass: 'text-lg font-bold',
    defaultIconKey: 'trophy',
  },
  alert: {
    iconBg: '#FFE5E5',
    iconColor: '#E63333',
    accentBar: '#E63333',
    titleColor: '#E63333',
    titleClass: 'text-base font-semibold',
    defaultIconKey: 'flame',
  },
  coaching: {
    iconBg: '#E5F9FF',
    iconColor: '#0080B8',
    bennyPose: 'thinking',
    titleColor: '#1F2937',
    titleClass: 'text-base font-semibold',
    defaultIconKey: 'lightbulb',
  },
  progress: {
    iconBg: '#F0FFE5',
    iconColor: '#B4BF74',
    titleColor: '#1F2937',
    titleClass: 'text-base font-semibold',
    defaultIconKey: 'trending-up',
  },
  shift: {
    iconBg: '#E5F9FF',
    iconColor: '#0080B8',
    titleColor: '#1F2937',
    titleClass: 'text-base font-semibold',
    defaultIconKey: 'calendar',
  },
  manager: {
    iconBg: '#FFE5F9',
    iconColor: '#EE99C2',
    titleColor: '#1F2937',
    titleClass: 'text-base font-semibold',
    defaultIconKey: 'users',
  },
  system: {
    iconBg: '#F3F4F6',
    iconColor: '#6B7280',
    titleColor: '#6B7280',
    titleClass: 'text-base font-medium',
    defaultIconKey: 'bell',
  },
};

const typeToVariant: Record<string, NotificationVariant> = {
  badge_unlocked: 'celebration',
  challenge_completed: 'celebration',
  streak_milestone: 'celebration',
  streak_at_risk: 'alert',
  shift_no_show: 'alert',
  coaching_tip: 'coaching',
  upsell_opportunity: 'coaching',
  challenge_almost_done: 'progress',
  sales_milestone: 'progress',
  pre_shift_reminder: 'shift',
  shift_starting_soon: 'shift',
  shift_ended_summary: 'shift',
  roster_changed: 'shift',
  server_performance_drop: 'manager',
  device_offline: 'manager',
  welcome: 'system',
};

const adminMessageOverrides: Record<string, NotificationVariant> = {
  celebration: 'celebration',
  manager: 'manager',
  coaching: 'coaching',
  alert: 'alert',
  system: 'system',
  shift: 'shift',
  progress: 'progress',
};

const typeIconOverrides: Record<string, IconKey> = {
  streak_at_risk: 'flame',
  streak_milestone: 'flame',
  badge_unlocked: 'award',
  challenge_completed: 'trophy',
  challenge_almost_done: 'trophy',
  sales_milestone: 'dollar',
  shift_no_show: 'alert-triangle',
  device_offline: 'wifi-off',
  server_performance_drop: 'trending-down',
  welcome: 'party',
  coaching_tip: 'lightbulb',
  upsell_opportunity: 'lightbulb',
};

export function variantForType(type: string, payload?: Record<string, unknown>): NotificationVariant {
  if (type === 'admin_message') {
    const v = (payload?.variant as string | undefined)?.toLowerCase() ?? 'system';
    return adminMessageOverrides[v] ?? 'system';
  }
  return typeToVariant[type] ?? 'system';
}

export function presetFor(variant: NotificationVariant): VariantPreset {
  return variants[variant];
}

export function iconForType(type: string, variant: NotificationVariant): IconKey {
  return typeIconOverrides[type] ?? presetFor(variant).defaultIconKey;
}
